import { Dialog, Button } from '@material-ui/core'
import login from 'pages/login'
import React, { useCallback, useEffect, useState } from 'react'
import GroupIcon from '@material-ui/icons/Group'
import {
  DialogContentCenterbody,
  BottomContent,
  ButtonContainer,
  ImpersonateDialog,
  Title,
  CenterContent,
  SpacedGroup,
  CenteredIconContainer,
} from './Impersonate.styled'
import useAccessPermissions from 'hooks/useAccessToken/useAccessPermissions'
import { XAPIPermissions } from 'types/custom.d.ts'
import useLoggedNavigate from 'hooks/useLoggedNavigate'
import { useProfile } from 'Contexts/ProfileContext'
import APIService from 'services/api-service'
import { useAuth0 } from '@auth0/auth0-react'
import { CircularProgress } from '@mui/material'
import LoginService from 'services/login-service'

function Impersonate() {
  const permissions = useAccessPermissions()
  const profileContext = useProfile()
  const { logout, loginWithRedirect, isAuthenticated, isLoading } = useAuth0()
  const nav = useLoggedNavigate()

  const [automaticProfileID, setAutomaticProfileID] = useState<string | null>(
    null
  )

  const [automaticContactIDLoading, setAutomaticProfileLoading] =
    useState(false)

  //Handle the automatic detection of the profile ID. This should set it and account for all cases.
  useEffect(() => {
    async function logOutUnimpersonatorsWithCallback() {
      if (
        isLoading ||
        !permissions.loaded ||
        permissions.permissions.has(XAPIPermissions.ImpersonateDatasightUser)
      ) {
        return false
      }

      const params = new URLSearchParams(window.location.search)
      const profileIDParam = params.get('ProfileID')?.trim() ?? ''

      //They're not an imposter, and not trying to impersonate anyone. They wandered here.
      if (!profileIDParam) {
        // nav('/')
        return false
      }

      const redirectURL = `${window.location.origin}/impersonate?ProfileID=${profileIDParam}`

      if (isAuthenticated) {
        console.log('Logging Out - Impersonation Request')
        await logout({
          logoutParams: {
            returnTo: redirectURL,
          },
        })

        return true
      }

      console.log('Unauthenticated, redirecting to login (Impersonation)')
      await loginWithRedirect({
        appState: {
          returnTo: redirectURL,
        },
      })

      return true
    }

    function getAndSetProfileIDFromParameters() {
      if (
        !!automaticProfileID ||
        automaticContactIDLoading ||
        !permissions.loaded ||
        !permissions.permissions.has(XAPIPermissions.ImpersonateDatasightUser)
      ) {
        return null
      }

      const params = new URLSearchParams(window.location.search)
      const profileIDParam = params.get('ProfileID')?.trim()

      if (profileIDParam) {
        return profileIDParam
      }

      return null
    }

    function getProfileIDFromSessionStorage() {
      if (
        !!automaticProfileID ||
        automaticContactIDLoading ||
        !permissions.loaded ||
        !permissions.permissions.has(XAPIPermissions.ImpersonateDatasightUser)
      ) {
        return null
      }

      const storedContactID = localStorage.getItem('profileID')
      if (storedContactID) {
        return storedContactID
      }

      return null
    }

    function getExistingProfileID() {
      if (
        !!automaticProfileID ||
        automaticContactIDLoading ||
        !permissions.loaded ||
        !permissions.permissions.has(XAPIPermissions.ImpersonateDatasightUser)
      ) {
        return null
      }

      const profileID = LoginService.getImpersonationProfileID()
      return profileID
    }

    async function handlePageLoad() {
      const result = await logOutUnimpersonatorsWithCallback()

      if (result) {
        return
      }

      setAutomaticProfileLoading(true)

      const functions = [
        getAndSetProfileIDFromParameters,
        getProfileIDFromSessionStorage,
        getExistingProfileID,
      ]

      for (const f of functions) {
        const profileID = f()

        if (!profileID) {
          continue
        }

        setAutomaticProfileID(profileID)
        break
      }

      setAutomaticProfileLoading(false)
    }

    handlePageLoad()
  })

  //Once a profileID is determined AND the profile context  function for accepting the profile ID is set up, set the imposter profile ID and navigate to callback.
  useEffect(() => {
    if (
      automaticProfileID != null &&
      profileContext.utilities.setImposterProfileID
    ) {
      const params = new URLSearchParams(window.location.search)
      let callbackParam = decodeURIComponent(
        params.get('PostLoginURL')?.trim() ?? ''
      )

      callbackParam = callbackParam.startsWith('/')
        ? callbackParam.slice(1)
        : callbackParam

      profileContext.utilities.setImposterProfileID(automaticProfileID)
      profileContext.utilities.reloadProfile()
      setAutomaticProfileID(null)
      nav(`/${callbackParam}`)
    }
  }, [
    automaticProfileID,
    profileContext.dependentData.userDetails.imposterProfileID,
    profileContext.utilities.setImposterProfileID,
  ])

  const login = useCallback(async () => {
    await logout()
  }, [])

  if (
    !permissions.loaded ||
    automaticProfileID !== null ||
    automaticContactIDLoading
  ) {
    return
  }

  return (
    <Dialog open={true}>
      <Title>Impersonate a User</Title>
      <ImpersonateDialog>
        <DialogContentCenterbody>
          {automaticContactIDLoading && <CircularProgress />}
          {!automaticContactIDLoading && (
            <SpacedGroup>
              <CenteredIconContainer>
                <GroupIcon style={{ fontSize: '128px' }} />
              </CenteredIconContainer>
              <CenterContent>
                You are no longer impersonating a Datasight user.
                <br />
                To impersonate another user, please follow a link from the LIMS
                system.
                <br />
                <br />
                You may now close this page, or leave it open for future
                impersonations.
              </CenterContent>
            </SpacedGroup>
          )}
        </DialogContentCenterbody>

        <BottomContent>
          This page allows for the impersonation of Datasight users by internal
          users with impersonation access only. To impersonate a user, follow a
          link from the LIMS system.
        </BottomContent>
        <ButtonContainer>
          <Button variant="contained" color="primary" onClick={login} fullWidth>
            Logout
          </Button>
          {/* <Button
            variant="contained"
            color="secondary"
            disabled={}
            onClick={login}
          >
            Impersonate Selected User
          </Button> */}
        </ButtonContainer>
      </ImpersonateDialog>
    </Dialog>
  )
}

export default Impersonate
